import { motion } from 'framer-motion';
import { Button } from '@/shared/components/ui/button';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { Gem, MapPin, MessageSquare, Send, Star } from 'lucide-react';
import { NavigatorReasonCallout } from '@/shared/components/navigator-results';
import { DeliveryModeChips } from '@/shared/components/navigator-results/DeliveryModeChips';
import type { DeliveryMode } from '@/shared/components/navigator-results/navigator-results.types';
import { popIn } from '@/shared/motion/motionPresets';
import { useSpMessageLaunch } from '@/features/messaging/hooks/useSpMessageLaunch';
import type { ProviderProfile } from '@/features/provider-directory/types/provider-profile.types';
import type { useFpQaProviderProfiles } from '../../hooks/useFpProviderProfiles';

type MatchedProviderCardProps = {
  query: ReturnType<typeof useFpQaProviderProfiles>[number];
  isTopMatch?: boolean;
  /** Feature 080 — the FP swapped away from the AI top match (sage chip). */
  isSelection?: boolean;
  /** PROTOTYPE (087 ticket 10) — provider is in the global Best Match set. */
  isBestMatch?: boolean;
  /** AI rationale from the matchmaking result ("why this match"). */
  aiReason?: string | null;
  specialtyName: string;
  canRefer: boolean;
  onRefer: (profile: ProviderProfile) => void;
  /**
   * Feature 071 — send this provider's card to someone off-platform. Unlike
   * Refer, it needs no client: that is the point, and it is why this stays
   * enabled when `canRefer` is false for want of a selected client.
   */
  onSendContactCard?: (profile: ProviderProfile) => void;
  /** True when this engagement is explicitly tied to a client (spec §3.3). */
  isClientLinked?: boolean;
  /** Live per-specialty location from the navigator match — not directory union. */
  location?: string | null;
  /** Delivery modes for the searched specialty (108 ticket 02). */
  specialtyDeliveryModes?: DeliveryMode[];
};

const AVATAR_PALETTE = [
  'bg-sage',
  'bg-[hsl(169_50%_30%)]',
  'bg-[hsl(192_40%_35%)]',
  'bg-[hsl(215_30%_32%)]',
  'bg-[hsl(215_18%_45%)]',
];

export const MatchedProviderCard = ({
  query,
  isTopMatch = false,
  isSelection = false,
  isBestMatch = false,
  aiReason = null,
  specialtyName,
  canRefer,
  onRefer,
  onSendContactCard,
  isClientLinked = false,
  location: liveLocation = null,
  specialtyDeliveryModes,
}: MatchedProviderCardProps) => {
  // Computed ahead of the loading/error early returns so the launch-gate
  // hook (which must run unconditionally on every render) always has a
  // best-effort provider id/name, even before query.data resolves.
  const profileForHook = query.data;
  const personNameForHook = [profileForHook?.user?.firstName, profileForHook?.user?.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();
  const displayNameForHook =
    personNameForHook || profileForHook?.companyName?.trim() || 'Provider';
  const { launch: launchMessage, modal: messageModal } = useSpMessageLaunch({
    serviceProviderId: profileForHook?.id ?? '',
    providerName: displayNameForHook,
    sourceSurface: 'fp_qa_results',
    isClientLinked,
  });

  if (query.isLoading) {
    return <Skeleton className="h-44 w-full rounded-xl" />;
  }
  if (query.isError || !query.data) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-4">
        <p className="text-sm text-red-700">Provider details unavailable.</p>
      </div>
    );
  }

  const profile = query.data;
  const personName = personNameForHook;
  const companyLabel = profile.companyName?.trim() || '';
  const displayName = displayNameForHook;
  const role =
    personName && companyLabel
      ? companyLabel
      : profile.professionalTitle || specialtyName;
  const initials = getInitials(displayName);
  const avatarColor = AVATAR_PALETTE[hashString(displayName) % AVATAR_PALETTE.length];
  const location = liveLocation?.trim() || '';
  const rating =
    typeof profile.averageRating === 'number' && profile.averageRating > 0
      ? profile.averageRating.toFixed(1)
      : null;
  const description = (profile.tagline || profile.providerBio || '').trim();

  const featured = isTopMatch || isSelection;

  return (
    <article
      className={`relative overflow-hidden rounded-2xl border bg-card-base p-6 transition-shadow duration-150 ease-out motion-reduce:transition-none hover:shadow-[0_4px_14px_rgba(15,23,42,0.08)] ${
        featured ? 'border-sage/30 ring-1 ring-sage/25' : 'border-border-subtle'
      }`}
    >
      {featured && (
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-sage-bg to-transparent"
          aria-hidden
        />
      )}
      {/* Corner badge stack: gold foil + gem for the elevated cross-specialty
          tier (PROTOTYPE, 087 ticket 10). */}
      <div className="absolute right-5 top-5 flex flex-col items-end gap-1.5">
        {isBestMatch && (
          <motion.span
            variants={popIn}
            initial="initial"
            animate="animate"
            title="Highest-scoring provider across this care plan. Primary specialty breaks score ties."
            className="inline-flex items-center gap-1 rounded-md border border-[hsl(38_55%_68%)]/60 bg-gradient-to-br from-[hsl(43_75%_94%)] via-[hsl(38_78%_88%)] to-[hsl(30_68%_78%)] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.06em] text-[hsl(30_65%_26%)] shadow-[0_2px_8px_rgba(160,120,30,0.25)]"
          >
            <Gem className="h-3 w-3" aria-hidden />
            Best Match
          </motion.span>
        )}
        {isTopMatch && (
          <span
            title="Highest-ranked provider for this specialty."
            className="rounded-md bg-[hsl(40_75%_88%)] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.06em] text-[hsl(35_55%_28%)]"
          >
            Top Match
          </span>
        )}
        {!isTopMatch && isSelection && (
          <span className="rounded-md bg-sage px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.06em] text-white">
            Plan Selection
          </span>
        )}
      </div>

      <div className="relative flex items-start gap-5">
        <div
          className={`flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full text-base font-semibold text-white ${avatarColor}`}
          aria-hidden
        >
          {initials}
        </div>

        <div className="min-w-0 flex-1 pr-24">
          <h3 className="font-termina font-medium text-lg text-text-primary">{displayName}</h3>
          {role && <p className="text-sm text-text-secondary">{role}</p>}

          {(location || rating || specialtyDeliveryModes?.length) && (
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-text-secondary">
              {location && (
                <span className="flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5" aria-hidden />
                  {location}
                </span>
              )}
              {rating && (
                <span className="flex items-center gap-1">
                  <Star className="h-3.5 w-3.5 fill-sage text-sage" aria-hidden />
                  <span className="text-text-primary">{rating}</span>
                </span>
              )}
              <DeliveryModeChips modes={specialtyDeliveryModes} />
            </div>
          )}

          {aiReason?.trim() && <NavigatorReasonCallout reason={aiReason} />}

          {description && (
            <p className="mt-3 line-clamp-2 text-sm text-text-secondary">{description}</p>
          )}

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <Button
              size="sm"
              disabled={!canRefer}
              onClick={() => onRefer(profile)}
              title={
                canRefer
                  ? 'Refer this provider to the client'
                  : 'Select a client at Q&A start to enable referrals'
              }
              className="h-9 rounded-full bg-sage px-4 text-sm text-white transition-colors duration-150 ease-out hover:bg-[hsl(169_95%_18%)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-emphasis disabled:bg-sage/40"
            >
              <Send className="mr-1.5 h-3.5 w-3.5" aria-hidden />
              Refer
            </Button>
            {onSendContactCard && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onSendContactCard(profile)}
                title="Send this provider's contact card to someone without a bQuest account"
                className="h-9 rounded-full border-sage bg-transparent px-4 text-sm text-sage transition-colors duration-150 ease-out hover:bg-sage-bg hover:text-sage focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-emphasis"
              >
                <Send className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                Contact card
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={() => void launchMessage()}
              className="h-9 rounded-full border-sage bg-transparent px-4 text-sm text-sage transition-colors duration-150 ease-out hover:bg-sage-bg hover:text-sage focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-emphasis"
            >
              <MessageSquare className="mr-1.5 h-3.5 w-3.5" aria-hidden />
              Message
            </Button>
            <a
              href={`/providers/${profile.id}?discoverySource=FP_QA`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-9 items-center rounded-full px-2 text-sm font-medium text-text-secondary transition-colors duration-150 ease-out hover:text-sage focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-emphasis"
            >
              View profile
            </a>
          </div>
        </div>
      </div>
      {messageModal}
    </article>
  );
};

const getInitials = (name: string): string => {
  const parts = name.replace(/[^a-zA-Z\s]/g, '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const hashString = (input: string): number => {
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    hash = (hash * 31 + input.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
};
